'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useTransition } from 'react';
import { motion } from 'motion/react';
import { Loader2, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { cn } from '~/utils';

/** Per-character reveal speed and how long a fully-typed suggestion lingers before the next. */
const TYPE_CHAR_MS = 45;
const HOLD_FULL_MS = 1800;
type Props = {
  href: string;
  placeholderPrefix: string;
  suggestions: readonly string[];
  aiSearchLabel: string;
  ariaLabel: string;
  /** `glass` = standalone dark hero pill. `light` = borderless, embedded in a white bar. */
  surface?: 'glass' | 'light';
  className?: string;
  /** When set, submitting calls this with the trimmed value instead of navigating to `href`. */
  onSubmit?: (value: string) => void;
  /** Control rendered at the start of the bar (e.g. the AI mode toggle). */
  leading?: React.ReactNode;
  /** Control rendered just before the submit button (e.g. the chat-history dropdown). */
  trailing?: React.ReactNode;
  /** Seeds the input (e.g. a prompt carried over from another page). */
  initialValue?: string;
};
export const HeaderAiSearchBar: React.FC<Props> = t0 => {
  const $ = _c(62);
  const {
    href,
    placeholderPrefix,
    suggestions,
    aiSearchLabel,
    ariaLabel,
    surface: t1,
    className,
    onSubmit,
    leading,
    trailing,
    initialValue
  } = t0;
  const surface = t1 === undefined ? "glass" : t1;
  const isLight = surface === "light";
  const router = useRouter();
  const [isNavigating, startNavigation] = useTransition();
  const [value, setValue] = useState(initialValue ?? "");
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);
  const isTyping = value.length > 0;
  const currentSuggestion = suggestions[index] ?? "";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = inputRef.current;
      if (!el) {
        return;
      }
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== value) {
    t3 = [value];
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== currentSuggestion || $[4] !== isTyping || $[5] !== suggestions.length || $[6] !== typed) {
    t4 = () => {
      if (isTyping || suggestions.length === 0) {
        return;
      }
      if (typed.length < currentSuggestion.length) {
        const id = setTimeout(() => setTyped(currentSuggestion.slice(0, typed.length + 1)), TYPE_CHAR_MS);
        return () => clearTimeout(id);
      }
      const id_0 = setTimeout(() => {
        setTyped("");
        setIndex(i => (i + 1) % suggestions.length);
      }, HOLD_FULL_MS);
      return () => clearTimeout(id_0);
    };
    t5 = [typed, currentSuggestion, isTyping, suggestions.length];
    $[3] = currentSuggestion;
    $[4] = isTyping;
    $[5] = suggestions.length;
    $[6] = typed;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const [seededValue, setSeededValue] = useState(initialValue);
  if (initialValue !== seededValue) {
    setSeededValue(initialValue);
    if (initialValue && initialValue.length > 0) {
      setValue(initialValue);
    }
  }
  let t6;
  if ($[9] !== href || $[10] !== onSubmit || $[11] !== router || $[12] !== value) {
    t6 = e => {
      e.preventDefault();
      const trimmed = value.trim();
      if (onSubmit) {
        if (trimmed.length === 0) {
          return;
        }
        onSubmit(trimmed);
        setValue("");
        return;
      }
      const url = trimmed.length > 0 ? `${href}&startPrompt=${encodeURIComponent(trimmed)}` : href;
      startNavigation(() => router.push(url));
    };
    $[9] = href;
    $[10] = onSubmit;
    $[11] = router;
    $[12] = value;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSubmit = t6;
  let t7;
  if ($[14] !== className || $[15] !== isLight) {
    t7 = cn("flex w-full items-center gap-3 rounded-full text-left text-sm font-semibold lg:gap-4", isLight ? "max-lg:flex-wrap max-lg:items-start max-lg:gap-y-2 bg-transparent py-1 text-primary lg:px-2" : ["max-w-xl bg-white/5 py-2 pl-4 pr-2 text-white", "ring-1 ring-white/15 backdrop-blur-md", "transition-colors focus-within:bg-white/10 focus-within:ring-white/30"], className);
    $[14] = className;
    $[15] = isLight;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => inputRef.current?.focus();
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== leading) {
    t9 = leading && <div role="presentation" className="flex shrink-0 items-center" onClick={_temp}>{leading}</div>;
    $[18] = leading;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = isLight ? "max-lg:mt-2 max-lg:ml-1 max-lg:self-start text-gray-400" : "text-white/70";
  let t11;
  if ($[20] !== t10) {
    t11 = cn("size-5 shrink-0", t10);
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <Search className={t11} strokeWidth={2.5} aria-hidden={true} />;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_1 => setValue(e_1.target.value);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = isLight && "max-lg:min-h-[4.5rem]";
  const t15 = isLight ? "text-primary" : "text-white";
  const t16 = !isTyping && "caret-transparent";
  let t17;
  if ($[25] !== t14 || $[26] !== t15 || $[27] !== t16) {
    t17 = cn("min-w-0 flex-1 resize-none border-0 bg-transparent px-0 py-1 text-sm font-semibold leading-normal lg:text-base", "hide-scrollbar max-h-28 overflow-y-auto", t14, t15, "outline-none ring-0 focus:ring-0", t16);
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== ariaLabel || $[30] !== t17 || $[31] !== value) {
    t18 = <textarea ref={inputRef} data-testid="ai-chat-hero-input" value={value} rows={1} onChange={t13} onKeyDown={_temp2} className={t17} autoComplete="off" enterKeyHint="search" aria-label={ariaLabel} />;
    $[29] = ariaLabel;
    $[30] = t17;
    $[31] = value;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== isLight || $[34] !== isTyping || $[35] !== placeholderPrefix || $[36] !== typed) {
    t19 = !isTyping && <div className={cn("pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap text-sm leading-normal lg:text-base", isLight && "max-lg:items-start max-lg:pt-2", isLight ? "text-gray-400" : "text-white/60")} aria-hidden={true}><span className="min-w-0 truncate">{placeholderPrefix} {typed}</span><motion.span animate={{
        opacity: [1, 0.2, 1]
      }} transition={{
        duration: 1.1,
        repeat: Infinity,
        ease: "easeInOut"
      }} className={cn("ml-0.5 h-5 w-0.5 shrink-0 rounded-full", isLight ? "bg-gray-500" : "bg-white/80")} /></div>;
    $[33] = isLight;
    $[34] = isTyping;
    $[35] = placeholderPrefix;
    $[36] = typed;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t18 || $[39] !== t19) {
    t20 = <div className="relative flex min-w-0 flex-1 items-center">{t18}{t19}</div>;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== trailing) {
    t21 = trailing && <div role="presentation" className="flex shrink-0 items-center" onClick={_temp3}>{trailing}</div>;
    $[41] = trailing;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== isLight) {
    t22 = isLight && <span className="basis-full lg:hidden" aria-hidden={true} />;
    $[43] = isLight;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== isNavigating) {
    t23 = isNavigating ? <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden={true} /> : <Search className="size-4 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[45] = isNavigating;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== aiSearchLabel) {
    t24 = <span className="whitespace-nowrap">{aiSearchLabel}</span>;
    $[47] = aiSearchLabel;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== ariaLabel || $[50] !== t23 || $[51] !== t24) {
    t25 = <button type="submit" className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-4xl bg-tertiary px-4 text-sm font-semibold text-white transition-colors hover:bg-tertiary-700 max-lg:order-last max-lg:ml-auto lg:h-16 lg:px-6 lg:text-base" aria-label={ariaLabel}>{t23}{t24}</button>;
    $[49] = ariaLabel;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== handleSubmit || $[54] !== t12 || $[55] !== t20 || $[56] !== t21 || $[57] !== t22 || $[58] !== t25 || $[59] !== t7 || $[60] !== t9) {
    t26 = <form role="presentation" onSubmit={handleSubmit} className={t7} onClick={t8}>{t9}{t12}{t20}{t21}{t22}{t25}</form>;
    $[53] = handleSubmit;
    $[54] = t12;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t25;
    $[59] = t7;
    $[60] = t9;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  return t26;
};
function _temp(e_0) {
  return e_0.stopPropagation();
}
function _temp2(e_2) {
  if (e_2.key === "Enter" && !e_2.shiftKey) {
    e_2.preventDefault();
    e_2.currentTarget.form?.requestSubmit();
  }
}
function _temp3(e_3) {
  return e_3.stopPropagation();
}